import React, {
  forwardRef,
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import Player from "video.js/dist/types/player";

import Video from "./video";
import { ContentOverlayRef } from "../contentOverlay/contentOverlay";
import MediaLightbox, { MediaLightboxRef } from "../lightbox/mediaLightbox";

import "./videoBackgroundPlayer.scss";

interface VideoBackgroundPlayerProps {
  children?: React.ReactNode;
  muted: boolean;
  playing: boolean;
  placeholderImage?: Queries.kontent_item_asset_element;
  setMuted?: React.Dispatch<React.SetStateAction<boolean>>;
  setPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  src: {
    asset: {
      thumbnailUrl: string;
      type: string;
    };
    md: string;
    xl: string;
    xs: string;
  };
  transcriptRef?: MutableRefObject<ContentOverlayRef|null>;
  vttUrl?: string;
  title: string;
}

export interface VideoBackgroundPlayerRef {
  openVideo: () => void;
  toggleMute: () => void;
}

const VideoBackgroundPlayer = ({
  children = null,
  vttUrl = undefined,
  src,
  placeholderImage = undefined,
  muted,
  setMuted = () => {},
  playing,
  setPlaying,
  transcriptRef = undefined,
  title,
}: VideoBackgroundPlayerProps, ref: React.Ref<VideoBackgroundPlayerRef>) => {
  const lightbox = useRef<MediaLightboxRef>(null);
  const backgroundPlayer = useRef<Player>(null);

  useImperativeHandle(ref, () => ({
    openVideo() {
      setMuted(true);
      setPlaying(false);
      backgroundPlayer.current?.pause();
      lightbox.current?.show();
    },
    toggleMute() {
      setMuted((prevState) => !prevState);
    },
  }), []);

  useEffect(() => {
    if (backgroundPlayer?.current) {
      backgroundPlayer.current.muted(muted);
    }
  }, [muted]);

  useEffect(() => {
    if (backgroundPlayer?.current) {
      backgroundPlayer.current.loop(playing);
      backgroundPlayer.current.autoplay(playing);

      if (playing) {
        backgroundPlayer.current.play();
      } else {
        backgroundPlayer.current.pause();
      }
    }
  }, [playing]);

  const sources = {
    md: src?.md ? { src: src.md, type: src.asset.type } : undefined,
    xl: src?.xl ? { src: src.xl, type: src.asset.type } : undefined,
    xs: src?.xs ? { src: src.xs, type: src.asset.type } : undefined,
  };

  const placeholder = (placeholderImage && placeholderImage.url) || src?.asset.thumbnailUrl || "/images/placeholder/placeholder.webp";

  return (
    <>
      <Video
        options={{
          autoplay: true,
          controls: false,
          loop: true,
          muted,
          playsinline: true,
          poster: placeholder,
          responsive: true,
        }}
        vttUrl={vttUrl}
        sources={sources}
        title={title}
        asBackground
        onReady={(player) => {
          (backgroundPlayer as React.MutableRefObject<Player>).current = player;
        }}
      />
      <div className="video-background-player">
        {children}
      </div>
      <MediaLightbox
        video
        onHide={() => {
          setPlaying(true);
          transcriptRef?.current?.close();
        }}
        ref={lightbox}
        withConsent={false}
        media={(
          <Video
            transcriptRef={transcriptRef}
            options={{
              autoplay: true,
              controlBar: {
                children: [
                  "playToggle",
                  "volumePanel",
                  "currentTimeDisplay",
                  "timeDivider",
                  "durationDisplay",
                  "progressControl",
                  "subtitlesButton",
                  "fullscreenToggle",
                ],
              },
              controls: true,
              fluid: true,
              loop: false,
              muted: false,
              poster: placeholder,
              responsive: true,
            }}
            title={title}
            vttUrl={vttUrl}
            sources={sources}
          />
        )}
      />
    </>
  );
};

export default forwardRef<VideoBackgroundPlayerRef, VideoBackgroundPlayerProps>(VideoBackgroundPlayer);
